import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { DashboardOut, OutletRevenueOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { FilterBar } from '../components/FilterBar'
import { Kpi, Trend } from '../components/Kpi'
import { Panel } from '../components/States'
import { AXIS, ChartTip, GRID, SERIES_COLORS } from '../components/Charts'
import { useFilters } from '../lib/filters'
import {
  count,
  delta,
  formatDate,
  hourLabel,
  money,
  moneyCompact,
  moneyWhole,
  num,
  percent,
  shortDate,
} from '../lib/format'

export function DashboardPage() {
  const { filters } = useFilters()
  const [search] = useSearchParams()

  const params = {
    from_date: filters.from,
    to_date: filters.to,
    outlet_id: filters.outletId,
    scope: filters.scope,
  }

  const dashboard = useQuery({
    queryKey: ['dashboard', params, filters.compare],
    queryFn: () => api.get<DashboardOut>('/analytics/dashboard', { ...params, compare: filters.compare }),
  })

  const outlets = useQuery({
    queryKey: ['by-outlet', filters.from, filters.to, filters.scope, filters.compare],
    queryFn: () =>
      api.get<OutletRevenueOut>('/analytics/revenue/by-outlet', {
        from_date: filters.from,
        to_date: filters.to,
        scope: filters.scope,
        compare: filters.compare,
      }),
  })

  const data = dashboard.data
  const kpi = data?.kpi
  const comparison = data?.comparison

  // Profit is hidden unless the account may see it: the backend sends
  // gross_profit_coverage_pct as null in that case, and zeroes the figure.
  const showProfit = kpi?.gross_profit_coverage_pct !== null && kpi?.gross_profit_coverage_pct !== undefined

  /**
   * The comparison window has its own dates, so the two series are lined up by
   * position rather than by date — day 1 against day 1.
   */
  const salesSeries = useMemo(() => {
    const current = data?.sales_series ?? []
    const previous = comparison?.sales_series ?? []
    return current.map((point, index) => ({
      date: point.date,
      label: shortDate(point.date),
      total: num(point.total),
      invoices: point.invoice_count,
      previous: previous[index] ? num(previous[index]!.total) : null,
    }))
  }, [data?.sales_series, comparison?.sales_series])

  const topProducts = useMemo(
    () =>
      (data?.product_breakdown ?? []).slice(0, 10).map((row) => ({
        name: row.name.length > 28 ? `${row.name.slice(0, 27)}…` : row.name,
        fullName: row.name,
        revenue: num(row.revenue),
        quantity: row.quantity,
      })),
    [data?.product_breakdown],
  )

  const payments = useMemo(
    () =>
      (data?.payment_breakdown ?? []).map((row) => ({
        name: row.payment_option_name || 'Unspecified',
        value: num(row.amount),
      })),
    [data?.payment_breakdown],
  )

  const weekdays = useMemo(
    () =>
      (data?.time_by_weekday ?? []).map((row) => ({
        label: row.label,
        total: num(row.total),
        count: row.count,
      })),
    [data?.time_by_weekday],
  )

  const hours = useMemo(
    () =>
      (data?.time_by_hour ?? []).map((row) => ({
        label: hourLabel(row.hour),
        total: num(row.total),
        count: row.count,
      })),
    [data?.time_by_hour],
  )

  const teamTotal = (data?.team_breakdown ?? []).reduce((sum, row) => sum + num(row.sales_ex_tax), 0)

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`${formatDate(filters.from)} – ${formatDate(filters.to)}${
          filters.scope === 'fulfilled' ? ' · fulfilled sales only' : ''
        }`}
      />

      <div className="content stack">
        <FilterBar showCompare />

        <Panel isLoading={dashboard.isPending} error={dashboard.error}>
          {kpi ? (
            <>
              <div className="grid cols-4">
                <Kpi
                  label="Sales (ex. tax)"
                  value={money(kpi.sales_ex_tax)}
                  foot={
                    comparison ? (
                      <Trend
                        current={num(kpi.sales_ex_tax)}
                        previous={num(comparison.kpi.sales_ex_tax)}
                      />
                    ) : null
                  }
                />
                <Kpi
                  label="Average sale"
                  value={money(kpi.avg_sale_value)}
                  foot={
                    comparison ? (
                      <Trend
                        current={num(kpi.avg_sale_value)}
                        previous={num(comparison.kpi.avg_sale_value)}
                      />
                    ) : null
                  }
                />
                <Kpi
                  label="Invoices"
                  value={count(data?.sales_series.reduce((sum, point) => sum + point.invoice_count, 0))}
                  foot={
                    comparison ? (
                      <Trend
                        current={data?.sales_series.reduce((s, p) => s + p.invoice_count, 0) ?? 0}
                        previous={comparison.kpi.invoice_count}
                      />
                    ) : null
                  }
                />
                <Kpi label="New customers" value={count(kpi.new_customers)} />

                {showProfit ? (
                  <Kpi
                    label="Gross profit"
                    value={money(kpi.gross_profit)}
                    foot={`from ${percent(kpi.gross_profit_coverage_pct)} of lines with cost data`}
                  />
                ) : null}

                <Kpi label="Refunds" value={money(kpi.refunds_total)} />
                <Kpi label="Layby outstanding" value={money(kpi.layby_outstanding)} />
                <Kpi
                  label="Awaiting action"
                  value={count(kpi.awaiting_scheduling_count + kpi.awaiting_pickup_count)}
                  foot={`${count(kpi.awaiting_scheduling_count)} to schedule · ${count(
                    kpi.awaiting_pickup_count,
                  )} to collect`}
                />
              </div>

              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-head">
                  <h2>Sales over time</h2>
                  {comparison ? (
                    <span className="hint">
                      dashed: {formatDate(comparison.from_date)} – {formatDate(comparison.to_date)}
                    </span>
                  ) : null}
                </div>
                <div className="card-body">
                  <Panel
                    isLoading={false}
                    error={null}
                    isEmpty={salesSeries.length === 0}
                    emptyText="No sales in this period."
                  >
                    <div className="chart tall">
                      <ResponsiveContainer>
                        <ComposedChart data={salesSeries} margin={{ top: 6, right: 10, bottom: 0, left: 6 }}>
                          <defs>
                            <linearGradient id="salesFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.22} />
                              <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid {...GRID} vertical={false} />
                          <XAxis dataKey="label" {...AXIS} minTickGap={26} />
                          <YAxis {...AXIS} width={62} tickFormatter={(value) => moneyCompact(value)} />
                          <Tooltip
                            content={<ChartTip format={(_, value) => money(value)} />}
                            cursor={{ stroke: 'var(--border-strong)' }}
                          />
                          {/* The area is drawn first so its fill cannot paint
                              over the comparison line — Recharts paints in
                              render order. */}
                          <Area
                            type="monotone"
                            dataKey="total"
                            name="Sales"
                            stroke="var(--brand)"
                            strokeWidth={2}
                            fill="url(#salesFill)"
                          />
                          {comparison ? (
                            <Line
                              type="monotone"
                              dataKey="previous"
                              name="Previous"
                              stroke="var(--grey)"
                              strokeDasharray="4 3"
                              strokeWidth={1.5}
                              dot={false}
                              connectNulls
                            />
                          ) : null}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="grid cols-2" style={{ marginTop: 14 }}>
                <div className="card">
                  <div className="card-head">
                    <h2>Top products</h2>
                    <Link className="hint" to="/products">
                      All products →
                    </Link>
                  </div>
                  <div className="card-body">
                    <Panel isLoading={false} error={null} isEmpty={topProducts.length === 0}>
                      <div className="chart">
                        <ResponsiveContainer>
                          <BarChart
                            data={topProducts}
                            layout="vertical"
                            margin={{ top: 2, right: 12, bottom: 2, left: 4 }}
                          >
                            <CartesianGrid {...GRID} horizontal={false} />
                            <XAxis
                              type="number"
                              {...AXIS}
                              tickFormatter={(value) => moneyCompact(value)}
                            />
                            <YAxis type="category" dataKey="name" {...AXIS} width={150} />
                            <Tooltip
                              content={<ChartTip format={(_, value) => money(value)} />}
                              cursor={{ fill: 'var(--page)' }}
                            />
                            <Bar dataKey="revenue" name="Revenue" fill="var(--brand)" radius={[0, 3, 3, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Panel>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Payment mix</h2>
                  </div>
                  <div className="card-body">
                    <Panel isLoading={false} error={null} isEmpty={payments.length === 0}>
                      <div className="chart">
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={payments}
                              dataKey="value"
                              nameKey="name"
                              innerRadius="52%"
                              outerRadius="80%"
                              paddingAngle={2}
                              stroke="var(--surface)"
                              strokeWidth={2}
                            >
                              {payments.map((entry, index) => (
                                <Cell
                                  key={entry.name}
                                  fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip content={<ChartTip format={(_, value) => money(value)} />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="dl" style={{ marginTop: 10 }}>
                        {payments.map((entry, index) => (
                          <div key={entry.name} style={{ display: 'contents' }}>
                            <dt>
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 8,
                                  height: 8,
                                  borderRadius: 2,
                                  marginRight: 6,
                                  background: SERIES_COLORS[index % SERIES_COLORS.length],
                                }}
                              />
                              {entry.name}
                            </dt>
                            <dd>{money(entry.value)}</dd>
                          </div>
                        ))}
                      </div>
                    </Panel>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-head">
                  <h2>Sales by team member</h2>
                  <Link className="hint" to={`/sales-team?${search.toString()}`}>
                    {data?.team_breakdown.length ?? 0} people — see the full sales team →
                  </Link>
                </div>
                <div className="card-body flush">
                  <Panel isLoading={false} error={null} isEmpty={(data?.team_breakdown.length ?? 0) === 0}>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Salesperson</th>
                            <th className="num">Sales (ex. tax)</th>
                            <th className="num">Invoices</th>
                            <th className="num">Average sale</th>
                            <th style={{ width: 160 }}>Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data?.team_breakdown.map((row) => (
                            <tr key={row.sales_person_id ?? row.name}>
                              <td className="strong">{row.name}</td>
                              <td className="num">{money(row.sales_ex_tax)}</td>
                              <td className="num">{count(row.invoice_count)}</td>
                              <td className="num">{money(row.avg_sale)}</td>
                              <td>
                                <div className="row" style={{ gap: 8 }}>
                                  <div className="bar" style={{ flex: 1 }}>
                                    <span style={{ width: `${Math.min(100, row.share_pct)}%` }} />
                                  </div>
                                  <span className="cell-sub" style={{ width: 42, textAlign: 'right' }}>
                                    {percent(row.share_pct)}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {teamTotal > 0 ? (
                          <tfoot>
                            <tr>
                              <td className="strong">Total</td>
                              <td className="num strong">{money(teamTotal)}</td>
                              <td colSpan={3} />
                            </tr>
                          </tfoot>
                        ) : null}
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>

              <div className="grid cols-2" style={{ marginTop: 14 }}>
                <div className="card">
                  <div className="card-head">
                    <h2>Busiest days</h2>
                  </div>
                  <div className="card-body">
                    <Panel isLoading={false} error={null} isEmpty={weekdays.length === 0}>
                      <div className="chart">
                        <ResponsiveContainer>
                          <BarChart data={weekdays} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
                            <CartesianGrid {...GRID} vertical={false} />
                            <XAxis dataKey="label" {...AXIS} />
                            <YAxis {...AXIS} width={58} tickFormatter={(value) => moneyCompact(value)} />
                            <Tooltip
                              content={<ChartTip format={(_, value) => money(value)} />}
                              cursor={{ fill: 'var(--page)' }}
                            />
                            <Bar dataKey="total" name="Sales" fill="var(--blue)" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Panel>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h2>Busiest hours</h2>
                  </div>
                  <div className="card-body">
                    <Panel isLoading={false} error={null} isEmpty={hours.length === 0}>
                      <div className="chart">
                        <ResponsiveContainer>
                          <BarChart data={hours} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
                            <CartesianGrid {...GRID} vertical={false} />
                            <XAxis dataKey="label" {...AXIS} interval={2} />
                            <YAxis {...AXIS} width={58} tickFormatter={(value) => moneyCompact(value)} />
                            <Tooltip
                              content={<ChartTip format={(_, value) => money(value)} />}
                              cursor={{ fill: 'var(--page)' }}
                            />
                            <Bar dataKey="total" name="Sales" fill="var(--teal)" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </Panel>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: 14 }}>
                <div className="card-head">
                  <h2>Revenue by outlet</h2>
                </div>
                <div className="card-body flush">
                  <Panel
                    isLoading={outlets.isPending}
                    error={outlets.error}
                    isEmpty={(outlets.data?.items.length ?? 0) === 0}
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Outlet</th>
                            <th className="num">Revenue</th>
                            <th className="num">Invoices</th>
                            <th className="num">Average sale</th>
                            <th className="num">vs previous</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outlets.data?.items.map((row) => (
                            <tr key={row.outlet_id ?? row.outlet_name ?? 'unknown'}>
                              <td className="strong">{row.outlet_name ?? 'Unassigned'}</td>
                              <td className="num">{money(row.revenue)}</td>
                              <td className="num">{count(row.invoice_count)}</td>
                              <td className="num">{money(row.avg_sale)}</td>
                              <td className="num">
                                {row.delta_pct === null ? (
                                  <span className="cell-sub">—</span>
                                ) : (
                                  <span
                                    className={`trend ${
                                      row.delta_pct > 0 ? 'up' : row.delta_pct < 0 ? 'down' : 'flat'
                                    }`}
                                  >
                                    {delta(row.delta_pct)}
                                  </span>
                                )}
                                {row.prev_revenue !== null ? (
                                  <div className="cell-sub">was {moneyWhole(row.prev_revenue)}</div>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
